import React, {
	type ReactNode,
	useRef,
	useContext,
	memo,
	useCallback,
	useState,
	useMemo,
	type MouseEvent,
	type KeyboardEvent,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import { Draggable } from 'react-beautiful-dnd';
import Button from '@atlaskit/button';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Popup from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	type Selection,
	useCollabStore,
	getPresenceNodeId,
	BADGE_INNER_POSITION as INNER,
	BADGE_OUTER_POSITION as OUTER,
} from '@atlassian/jira-business-collaboration';
import {
	openViewFromEmbed,
	useFetchUserTraitsOld,
	useUserTraits,
} from '@atlassian/jira-business-common';
import { CARD_COVERS_TYPE, SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useIsHighlighted } from '@atlassian/jira-business-issue-create';
import { usePrefetchIssueViewDataActions } from '@atlassian/jira-business-issue-view-critical-data-loader';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { CardSummary } from '@atlassian/jira-platform-card';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { GAP_BETWEEN_CARDS } from '../../../../common/constants';
import type { BoardIssueOld, BoardColumn } from '../../../../common/types';
import { ActionsMenuPopupContext } from '../../../../controllers/actions-menu-popup-context-old';
import { useJWMBoardFeatures } from '../../../../controllers/features-context';
import { useSelectedFields } from '../../../../controllers/fields-preference';
import { useIssueActionsContext } from '../../../../controllers/issue-actions-context-old';
import { CardBadge } from './card-badge';
import { CardContent } from './card-content';
import { CardCover } from './card-cover';
import InlineSiblingCreate from './inline-sibling-create';
import messages from './messages';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const stopPropagation = (event: any) => event.stopPropagation();
const returnNull = () => null;

type CardMenu = (arg1: {
	onContentResize: () => void;
	onClosePopup: () => void;
	onOpenPopup: () => void;
}) => ReactNode;

type Props = {
	issue: BoardIssueOld;
	isClone?: boolean;
	draggableIndex: number;
	canCreateIssues: boolean;
	column: BoardColumn;
	highlight?: string[];
	cardMenu: CardMenu | null;
	selections?: Selection[];
	isDragging?: boolean;
	isSiblingCreateFormOpen: boolean;
	toggleSiblingCreateForm: () => void;
};

export const Card = memo<Props>(
	({
		cardMenu,
		column,
		canCreateIssues,
		draggableIndex,
		highlight,
		isClone,
		isDragging = false,
		isSiblingCreateFormOpen,
		issue,
		toggleSiblingCreateForm,
		selections,
	}: Props) => {
		const { formatMessage } = useIntl();
		const { issueViewInteraction } = useJWMBoardFeatures();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const isCardCoversEditorEnabled = fg('card_covers_editor');

		const [userTraits, { setUserTraits }] = isCardCoversEditorEnabled
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useUserTraits()
			: [null, { setUserTraits: noop }];
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const fetchUserTraits = isCardCoversEditorEnabled ? useFetchUserTraitsOld() : returnNull;

		const shouldUseEmbedIssueClick = issueViewInteraction === 'openNewTab';
		const getIsHighlighted = useIsHighlighted();

		const { openPopupRef, setOpenPopupRef } = useContext(ActionsMenuPopupContext);
		// Keeps track of current popup
		const popupRef = useRef(null);

		const onEmbedIssueOpen = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), 'issue clicked', 'boardEmbedIssue');
			openViewFromEmbed({ key: SELECTED_ISSUE_PARAM, value: issue.key });
		}, [createAnalyticsEvent, issue]);

		const { onIssueClick } = useIssueActionsContext();
		const clickHandler = useCallback(
			(e: MouseEvent<HTMLElement>) => {
				e.stopPropagation();
				// best practice to use for react-beautiful-dnd (https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/how-we-use-dom-events.md)
				if (e.defaultPrevented !== true) {
					const withCmd = e.ctrlKey || e.metaKey;
					onIssueClick(issue, withCmd);
				}

				fireUIAnalytics(createAnalyticsEvent({}), 'issue clicked', 'boardIssue');
			},
			[onIssueClick, issue, createAnalyticsEvent],
		);

		const issueKeyClickHandler = useCallback(() => {
			onIssueClick(issue, false);
		}, [onIssueClick, issue]);

		let prefetchIssue: (issueKey: string) => void = noop;
		if (ff('jwm.preload-issue-view-critical-data')) {
			// eslint-disable-next-line react-hooks/rules-of-hooks
			({ prefetchIssue } = usePrefetchIssueViewDataActions());
		}

		// Reduce rendered elements by only rendering actions menu on hover
		const [shouldRenderMenu, setShouldRenderMenu] = useState(false);
		const [isOpen, setIsOpen] = useState(false);
		const shouldPersistActionsMenu = isOpen && openPopupRef === popupRef;

		const menuButtonRef = useRef<HTMLDivElement>(null);

		const hideMenuButton: React.FocusEventHandler<HTMLDivElement> = useCallback(
			(e) => {
				// If the next element to receive focus is the menu button
				// don't hide the button
				if (menuButtonRef.current === e.relatedTarget) {
					return;
				}

				setShouldRenderMenu(false);
			},
			[setShouldRenderMenu],
		);
		const showMenuButton: React.FocusEventHandler<HTMLDivElement> = useCallback(() => {
			setShouldRenderMenu(true);

			if (ff('jwm.preload-issue-view-critical-data')) {
				prefetchIssue(issue.key);
			}
		}, [issue.key, prefetchIssue]);

		const toggleMenu = useCallback(
			(e: MouseEvent) => {
				stopPropagation(e);

				// Close the already opened popup if it's not the current one
				if (isOpen && openPopupRef !== popupRef) {
					setIsOpen(false);
				}

				// Update the opened popup ref to the current one if it's not already set
				if (openPopupRef !== popupRef) {
					setOpenPopupRef(popupRef);
				}

				setIsOpen((open) => !open);
			},
			[isOpen, openPopupRef, setOpenPopupRef],
		);

		const handleKeyDown = useCallback(
			(event: KeyboardEvent<HTMLDivElement>) => {
				if (event.key === 'Enter' && event.target === event.currentTarget) {
					shouldUseEmbedIssueClick ? onEmbedIssueOpen() : onIssueClick(issue, false);
				}
			},
			[onIssueClick, issue, onEmbedIssueOpen, shouldUseEmbedIssueClick],
		);
		const isNewOptimisticIssue = useMemo(() => issue.key === '' || issue.key == null, [issue.key]);
		const shouldRenderMenuOnCard = useMemo(
			() => !isNewOptimisticIssue && shouldRenderMenu,
			[isNewOptimisticIssue, shouldRenderMenu],
		);

		const onClosePopup = useCallback(() => {
			setIsOpen(false);
		}, [setIsOpen]);

		const onOpenPopup = useCallback(() => {
			setIsOpen(true);
		}, [setIsOpen]);

		const popupContent = useCallback(
			({ update }: { update: () => void }) => (
				<MenuContainer onClick={toggleMenu}>
					{cardMenu &&
						cardMenu({
							onContentResize: update,
							onClosePopup,
							onOpenPopup,
						})}
				</MenuContainer>
			),
			[cardMenu, onClosePopup, onOpenPopup, toggleMenu],
		);

		const setUserTraitsIfNotAvailable = useCallback(async () => {
			if (!userTraits?.attributes?.length && userTraits?.attributes !== null) {
				const newTraits = await fetchUserTraits();
				if (newTraits !== false) {
					setUserTraits(newTraits);
				}
			}
		}, [userTraits, fetchUserTraits, setUserTraits]);

		const draggableContentRenderer = useCallback(
			(provided = {}) => {
				const content = (
					<CardContentContainer ref={popupRef}>
						<CardSummaryWrapper>
							<CardSummary text={issue.summary} shouldMenuRender highlight={highlight} />
						</CardSummaryWrapper>
						<Popup
							isOpen={shouldPersistActionsMenu}
							onClose={() => setIsOpen(false)}
							content={popupContent}
							placement="right-start"
							trigger={(triggerProps) => (
								<div
									{...triggerProps}
									data-testid="work-management-board.ui.board-old.column-old.card-old.actions-section"
									// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={triggerStyle}
									onMouseOver={setUserTraitsIfNotAvailable}
									onFocus={setUserTraitsIfNotAvailable}
								>
									{(shouldRenderMenuOnCard || shouldPersistActionsMenu) &&
										(issue.issueDetails?.coverMedia ? (
											<CardMenuButtonWithCover
												ref={menuButtonRef}
												iconBefore={<MoreIcon label={formatMessage(messages.cardActions)} />}
												onClick={toggleMenu}
											/>
										) : (
											<Button
												ref={menuButtonRef}
												iconBefore={<MoreIcon label={formatMessage(messages.cardActions)} />}
												onClick={toggleMenu}
											/>
										))}
								</div>
							)}
						/>
						<CardContent
							highlight={highlight}
							issue={issue}
							onIssueKeyClick={shouldUseEmbedIssueClick ? onEmbedIssueOpen : issueKeyClickHandler}
						/>
					</CardContentContainer>
				);

				const contentWithBadge = (
					<CardBadge selections={selections} position={draggableIndex === 0 ? INNER : OUTER}>
						{content}
					</CardBadge>
				);

				const isHighlighted = getIsHighlighted(issue.id);

				return (
					<CardContainer
						isDragging={isDragging}
						ref={(ref) => {
							// @ts-expect-error - TS2339 - Property 'innerRef' does not exist on type '{}'. | TS2339 - Property 'innerRef'
							provided.innerRef && provided.innerRef(ref);
						}}
						// @ts-expect-error - TS2339 - Property 'draggableProps' does not exist on type '{}'.
						{...provided.draggableProps}
						// @ts-expect-error - TS2339 - Property 'draggableProps' does not exist on type '{}'.
						{...provided.dragHandleProps}
						onClick={shouldUseEmbedIssueClick ? onEmbedIssueOpen : clickHandler}
						onMouseEnter={showMenuButton}
						onMouseLeave={hideMenuButton}
						onFocus={showMenuButton}
						onBlur={hideMenuButton}
						data-testid="work-management-board.ui.board-old.column-old.card-old.container"
						onKeyDown={handleKeyDown}
					>
						{issue.issueDetails?.coverMedia && (
							<CardCoverWrapper coverMedia={issue.issueDetails.coverMedia} />
						)}
						<HighlightContainer
							isDraggingAllowed={!isNewOptimisticIssue}
							isHighlighted={isHighlighted}
						>
							{selections ? contentWithBadge : content}
						</HighlightContainer>
					</CardContainer>
				);
			},
			[
				clickHandler,
				draggableIndex,
				formatMessage,
				getIsHighlighted,
				handleKeyDown,
				hideMenuButton,
				highlight,
				isDragging,
				isNewOptimisticIssue,
				issue,
				issueKeyClickHandler,
				onEmbedIssueOpen,
				popupContent,
				selections,
				shouldPersistActionsMenu,
				shouldRenderMenuOnCard,
				shouldUseEmbedIssueClick,
				showMenuButton,
				toggleMenu,
				setUserTraitsIfNotAvailable,
			],
		);

		if (isClone === true) {
			return draggableContentRenderer();
		}

		return (
			<>
				<Draggable
					draggableId={`ISSUE::${issue.id}`}
					index={draggableIndex}
					isDragDisabled={isNewOptimisticIssue}
				>
					{draggableContentRenderer}
				</Draggable>
				{canCreateIssues && (
					<InlineSiblingCreate
						column={column}
						isSiblingCreateFormOpen={isSiblingCreateFormOpen}
						issueId={issue.id}
						toggleSiblingCreateForm={toggleSiblingCreateForm}
					/>
				)}
			</>
		);
	},
);

type CardCoverWrapperProps = {
	coverMedia?: NonNullable<BoardIssueOld['issueDetails']>['coverMedia'];
};

const CardCoverWrapper = memo<CardCoverWrapperProps>(({ coverMedia }) => {
	const selectedFields = useSelectedFields();
	const showCardCovers = selectedFields[CARD_COVERS_TYPE];
	return showCardCovers && <CardCover coverMedia={coverMedia} />;
});

const CardWithSelection = memo<Props>((props) => {
	const projectData = useProject();
	const projectId = String(projectData.id);
	const nodeId = getPresenceNodeId({ projectId, viewId: 'board', nodeId: `${props.issue.id}` });
	const [{ nodeSelection }] = useCollabStore();
	const selections = nodeSelection[nodeId];
	// card could have multiple selections from different users
	const cardSelections = Array.isArray(selections) && selections.length ? selections : undefined;

	return <Card {...props} selections={cardSelections} />;
});

export default CardWithSelection;

const MAX_CARD_WIDTH = 270;
const ACTION_MENU_BUTTON_SIZE = 32;

const triggerStyle = {
	position: 'absolute',
	top: token('space.100', '8px'),
	right: token('space.100', '8px'),
} as const;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardMenuButtonWithCover = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.accent.gray.subtlest', colors.N0),

	'&:focus': {
		opacity: 1,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.accent.gray.subtlest.pressed', colors.N0),
	},

	'&:hover': {
		opacity: 1,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.accent.gray.subtlest.hovered', colors.N0),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[role='menu'] > div, [role='menu'] > [role='menuitem']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		padding: `${token('space.100', '8px')} ${token('space.150', '12px')} ${gridSize - 1}px`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardContainer = styled.div<{
	isDragging?: boolean;
}>({
	maxWidth: MAX_CARD_WIDTH,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: (props) => (props.isDragging ? 'grabbing' : 'pointer'),
	userSelect: 'none',
	boxSizing: 'border-box',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	margin: (props) => (props.isDragging ? 0 : `${GAP_BETWEEN_CARDS}px 0 0 0`),
	borderRadius: token('border.radius', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	boxShadow: (props) =>
		props.isDragging
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('elevation.shadow.overlay', `0px 8px 12px ${colors.N50A}, 0 0 1px ${colors.N60A}`)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('elevation.shadow.raised', `0 1px 1px ${colors.N50A}, 0 0 1px 1px ${colors.N40A}`),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) =>
		props.isDragging
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('elevation.surface.overlay', colors.N0)
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('elevation.surface.raised', colors.N0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	rotate: (props) => (props.isDragging ? '4deg' : 0),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral.subtle.hovered', colors.N20A),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardContentContainer = styled.div({
	padding: token('space.150', '12px'),
	boxSizing: 'border-box',
	display: 'flex',
	flexFlow: 'row wrap',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardSummaryWrapper = styled.div({
	display: 'flex',
	minHeight: `${ACTION_MENU_BUTTON_SIZE}px`,
	paddingBottom: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HighlightContainer = styled.div<{
	isDraggingAllowed: boolean;
	isHighlighted: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isHighlighted }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		isHighlighted ? token('color.background.accent.blue.subtler', colors.B200) : 'transparent',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isDraggingAllowed }) => (isDraggingAllowed ? 'inherit' : 'not-allowed'),
	transition: 'background-color 2s ease-out',
});
